import {TMTextMessagingPreferencePreference} from '@amicamutual/amica-common';
import {append, isEmpty, map, pipe, reject, split, trim, uniq} from 'ramda';

export enum PrototypeMode {
  NONE = 'NONE',
  QUOTING = 'QUOTING'
}

// The mutual-claims notification type is enabled by default and cannot be removed.
// This method of retrieving the currently available preferences allows us to keep
// mutual-claims by default regardless of values (or lack of) specified in:
// REACT_APP_ENABLED_NOTIFICATION_PREFERENCES env var
// EnabledTextingNotificationsPreferences firebase flag value
// EnabledTextingNotificationsPreferences remoteConfigDefault value.
export const getEnabledNotificationPreferences = (
  enabledNotificationPreferences: string = ''
): TMTextMessagingPreferencePreference[] =>
  pipe(
    split(/\s*,\s*/),
    append('Mutual_Claims'),
    map(trim),
    reject(isEmpty),
    uniq
  )(String(enabledNotificationPreferences)) as TMTextMessagingPreferencePreference[];

const getPrototypeMode = (mode?: string) => {
  switch (mode) {
    case 'QUOTING':
      return PrototypeMode.QUOTING;
    default:
      return PrototypeMode.NONE;
  }
};

export const isUnitTestMode = process.env.NODE_ENV === 'test';

export const isCypress = !!(window as any)?.Cypress;

export const isDevelopmentMode = process.env.NODE_ENV === 'development';

export const isProduction = process.env.REACT_APP_ENV === 'PROD';

export const StaticConfig = {
  adobeTargetKey: process.env.REACT_APP_ADOBE_TARGET_KEY || '',
  appVersion: process.env.REACT_APP_VERSION || '',
  baseApi: process.env.REACT_APP_BASE_API || '',
  baseAemApi: process.env.REACT_APP_BASE_AEM_API || '',
  chatBotDeploymentID: process.env.REACT_APP_CHAT_BOT_DEPLOYMENT_ID || '',
  chatBotTranscriptID: process.env.REACT_APP_CHAT_BOT_TRANSCRIPT_ID || '',
  ensightenUri: process.env.REACT_APP_ENSIGHTEN_URI || '',
  ensightenMobileAppUri: process.env.REACT_APP_ENSIGHTEN_MOBILE_APP_URI || '',
  firebaseApiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  firebaseAppId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  firebaseAuthDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  firebaseMessagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  firebaseRemoteConfigMinimumFetchInterval: !isNaN(
    parseInt(process.env.REACT_APP_FIREBASE_REMOTE_CONFIG_MINIMUM_FETCH_INTERVAL || '', 10)
  )
    ? parseInt(process.env.REACT_APP_FIREBASE_REMOTE_CONFIG_MINIMUM_FETCH_INTERVAL || '', 10)
    : 60000,
  firebaseStorageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  fnolAutoGlassVendorBaseUrl: process.env.REACT_APP_FNOL_AUTO_GLASS_VENDOR_BASE_URL || '',
  fnolAutoGlassVendorClientId: process.env.REACT_APP_FNOL_AUTO_GLASS_VENDOR_CLIENT_ID || '',
  calendlyMutualSalesURL: process.env.REACT_APP_CALENDLY_MUTUAL_SALES_URL || '',
  calendlyLifeSalesURL: process.env.REACT_APP_CALENDLY_LIFE_SALES_URL || '',
  calendlyWidgetURL: process.env.REACT_APP_CALENDLY_WIDGET_URL || '',
  foreseeBaseUri: process.env.REACT_APP_FORESEE_URI || '',
  googleAPIKey: process.env.REACT_APP_GOOGLE_API_KEY,
  homePageUrl: window.location.origin,
  mobileApplicationLoader:
    (window as any).remoteOverride === 'mobileApplicationLoader' || process.env.REACT_APP_MOBILE_APP_LOADER === 'true',
  mobileApplicationMode:
    ['mobileApplicationLoader', 'mobileApplicationMode'].includes((window as any).remoteOverride) ||
    process.env.REACT_APP_MOBILE_APP_LOADER === 'true' ||
    process.env.REACT_APP_MOBILE_APPLICATION === 'true',
  mobileFirebaseApiKey: process.env.REACT_APP_MOBILE_FIREBASE_API_KEY || '',
  mobileFirebaseAppId: process.env.REACT_APP_MOBILE_FIREBASE_APP_ID || '',
  mobileFirebaseAuthDomain: process.env.REACT_APP_MOBILE_FIREBASE_AUTH_DOMAIN || '',
  mobileFirebaseMessagingSenderId: process.env.REACT_APP_MOBILE_FIREBASE_MESSAGING_SENDER_ID || '',
  mobileFirebaseProjectId: process.env.REACT_APP_MOBILE_FIREBASE_PROJECT_ID || '',
  mobileFirebaseRemoteConfigMinimumFetchInterval: !isNaN(
    parseInt(process.env.REACT_APP_MOBILE_FIREBASE_REMOTE_CONFIG_MINIMUM_FETCH_INTERVAL || '', 10)
  )
    ? parseInt(process.env.REACT_APP_MOBILE_FIREBASE_REMOTE_CONFIG_MINIMUM_FETCH_INTERVAL || '', 10)
    : 60000,
  mobileFirebaseStorageBucket: process.env.REACT_APP_MOBILE_FIREBASE_STORAGE_BUCKET || '',
  notificationPreferences: getEnabledNotificationPreferences(process.env.REACT_APP_ENABLED_NOTIFICATION_PREFERENCES),
  oktaBaseUrl: process.env.REACT_APP_OKTA_BASE_URL || '',
  oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID || '',
  oktaClientIdAuthMessenger: process.env.REACT_APP_OKTA_CLIENT_ID_AUTH_MESSENGER || '',
  oktaIssuer: process.env.REACT_APP_OKTA_ISSUER || '',
  oneIncBaseUrl: process.env.REACT_APP_ONE_INC_BASE_URL || '',
  postLogoutURL: process.env.REACT_APP_POST_LOGOUT_URL || window.location.origin,
  prototypeMode: getPrototypeMode(process.env.REACT_APP_PROTOTYPE_MODE),
  quotingAEMAddressHandoff: process.env.REACT_APP_QUOTING_AEM_ADDRESS_HANDOFF_ENABLED === 'true',
  quotingBaseApi: process.env.REACT_APP_QUOTING_BASE_API || process.env.REACT_APP_BASE_API || '',
  quotingEffectiveDateRange: parseInt(process.env.REACT_APP_QUOTING_EFFECTIVE_DATE_RANGE || '', 10),
  forcedMFAMode: process.env.REACT_APP_FORCED_MFA_MODE === 'true',
  reactAppEnv: process.env.REACT_APP_ENV,
  edgeUUIDHeaderEnabled: process.env.REACT_APP_EDGE_UUID_HEADER_ENABLED === 'true',
  edgeUUIDHeaderAndCookieEnabled: process.env.REACT_APP_EDGE_UUID_HEADER_AND_COOKIE_ENABLED === 'true'
};

export const isNetlifyOrAwsMode =
  StaticConfig.homePageUrl.includes('netlify') || StaticConfig.homePageUrl.includes('amplifyapp');
